import { z } from "zod";
import { apenasDigitos } from "./validacao";
import { pedirJson } from "./api";

/* Consulta de CEP. Quem fala com ViaCEP e companhia é a API do portal (`GET /api/cep/{cep}`),
   não o navegador: sem CORS e com o fallback entre provedores decidido no servidor. */

export const esquemaDeCep = z.object({
  cep: z.string(),
  logradouro: z.string().nullable(),
  bairro: z.string().nullable(),
  cidade: z.string(),
  uf: z.string(),
  ibge: z.string().nullable(),
});

export type EnderecoDoCep = z.infer<typeof esquemaDeCep>;

/** Máscara de digitação: `88353100` vira `88353-100` conforme se digita. */
export function mascararCep(valor: string): string {
  const digitos = apenasDigitos(valor).slice(0, 8);
  return digitos.length > 5 ? `${digitos.slice(0, 5)}-${digitos.slice(5)}` : digitos;
}

export function cepCompleto(valor: string): boolean {
  return apenasDigitos(valor).length === 8;
}

export function consultarCep(cep: string, signal?: AbortSignal): Promise<EnderecoDoCep> {
  return pedirJson(`/cep/${apenasDigitos(cep)}`, esquemaDeCep, { signal });
}

export const CAMPOS_DO_CEP = ["logradouro", "bairro", "cidade", "uf"] as const;
export type CampoDoCep = (typeof CAMPOS_DO_CEP)[number];

type CamposDeEndereco = Record<CampoDoCep, string>;

/**
 * Aplica o resultado da consulta ao formulário: troca logradouro, bairro, cidade e UF, e deixa
 * como está tudo o mais (número, complemento, rótulo...). CEP de cidade pequena vem sem
 * logradouro/bairro; nesse caso o que a pessoa já digitou fica.
 *
 * `editados` são os campos que a pessoa mexeu DEPOIS de a consulta começar: a resposta chega
 * segundos depois da digitação e não pode atropelar o que ela acabou de escrever.
 */
export function mesclarEnderecoDoCep<T extends CamposDeEndereco>(
  atual: T,
  achado: EnderecoDoCep,
  editados: ReadonlySet<CampoDoCep> = new Set(),
): T {
  const novo: CamposDeEndereco = {
    logradouro: achado.logradouro || atual.logradouro,
    bairro: achado.bairro || atual.bairro,
    cidade: achado.cidade,
    uf: achado.uf.toUpperCase(),
  };
  const resultado = { ...atual };
  for (const campo of CAMPOS_DO_CEP) {
    if (!editados.has(campo)) resultado[campo] = novo[campo] as T[CampoDoCep];
  }
  return resultado;
}
